import React from "react";
import PropTypes from "prop-types";
import { formatAmount } from "../triangulation-format";

const DASH = "—";

// Reconciliation % between two values (min/max) — unit-invariant.
function matchPct(a, b) {
    const hi = Math.max(Number(a) || 0, Number(b) || 0);
    if (!hi) {
        return null;
    }
    return Math.round((Math.min(Number(a) || 0, Number(b) || 0) / hi) * 1000) / 10;
}

function matchBand(pct) {
    if (pct === null) {
        return "muted";
    }
    if (pct >= 70) {
        return "good";
    }
    if (pct >= 60) {
        return "warn";
    }
    return "bad";
}

// Month-over-month % change vs the previous period — unit-invariant.
function momPct(curr, prev) {
    if (
        curr === null ||
        curr === undefined ||
        prev === null ||
        prev === undefined ||
        !prev
    ) {
        return null;
    }
    return Math.round(((curr - prev) / prev) * 1000) / 10;
}

function Pct({ value }) {
    return value === null ? (
        <span className={"cc-kyc-empty"}>{DASH}</span>
    ) : (
        <span className={"cc-tri-pct cc-tri-pct--" + matchBand(value)}>
            {value}%
        </span>
    );
}

Pct.propTypes = { value: PropTypes.number };

// GST filing delay (days). Tone: 0 on-time (good), ≤30 late (warn), >30 (bad).
function FilingDelay({ value }) {
    if (value === null || value === undefined) {
        return <span className={"cc-kyc-empty"}>{DASH}</span>;
    }
    const band = value > 30 ? "bad" : value > 0 ? "warn" : "good";
    return (
        <span className={"cc-tri-pct cc-tri-pct--" + band}>{value}d</span>
    );
}

FilingDelay.propTypes = { value: PropTypes.number };

function ValueCell({ value, prev, unit }) {
    const mom = momPct(value, prev);
    const dir = mom === null ? null : mom > 0 ? "up" : mom < 0 ? "down" : "flat";
    return (
        <td>
            <div className={"cc-tri-cell"}>
                <span className={"cc-tri-cell__val"}>
                    {formatAmount(value, unit) || DASH}
                </span>
                {mom !== null ? (
                    <span className={"cc-tri-mom cc-tri-mom--" + dir}>
                        {dir === "up" ? "▲ " : dir === "down" ? "▼ " : ""}
                        {Math.abs(mom)}%
                    </span>
                ) : null}
            </div>
        </td>
    );
}

ValueCell.propTypes = {
    value: PropTypes.number,
    prev: PropTypes.number,
    unit: PropTypes.string
};

const sum = (rows, key) =>
    Math.round(rows.reduce((agg, r) => agg + (Number(r[key]) || 0), 0) * 10) / 10;

/**
 * Analyst numbers view of the triangulation. Ledger is split into Sales (vs GST)
 * and Repayment (vs Bank / BSA). Each figure shows its period-over-period
 * growth/degrowth plus reconciliation %s and a totals row. Amounts respect the
 * selected unit (lacs / cr / full).
 */
const TriangulationTable = ({ data, unit }) => {
    const totals = {
        gst: sum(data, "gst"),
        gstPurchase: sum(data, "gstPurchase"),
        ledger: sum(data, "ledger"),
        ledgerRepayment: sum(data, "ledgerRepayment"),
        bank: sum(data, "bank"),
        bankDebit: sum(data, "bankDebit")
    };
    return (
        <div className={"cc-tri-table-wrap"}>
            <table className={"cc-tri-table"}>
                <thead>
                    <tr>
                        <th>Period</th>
                        <th className={"cc-tri-table__src--gst"}>GST Sales</th>
                        <th className={"cc-tri-table__src--gst"}>
                            GST Purchase
                        </th>
                        <th className={"cc-tri-table__src--ledger"}>
                            Ledger Sales
                        </th>
                        <th className={"cc-tri-table__src--ledger"}>
                            Ledger Repayment
                        </th>
                        <th className={"cc-tri-table__src--bank"}>
                            Bank Credits
                        </th>
                        <th className={"cc-tri-table__src--bank"}>
                            Bank Debits
                        </th>
                        <th>Sales Match</th>
                        <th>Repay Match</th>
                        <th>Filing Delay (d)</th>
                    </tr>
                </thead>
                <tbody>
                    {data.map((row, i) => {
                        const prev = data[i - 1] || {};
                        return (
                            <tr key={row.month}>
                                <td className={"cc-tri-table__month"}>
                                    {row.month}
                                </td>
                                <ValueCell
                                    value={row.gst}
                                    prev={prev.gst}
                                    unit={unit}
                                />
                                <ValueCell
                                    value={row.gstPurchase}
                                    prev={prev.gstPurchase}
                                    unit={unit}
                                />
                                <ValueCell
                                    value={row.ledger}
                                    prev={prev.ledger}
                                    unit={unit}
                                />
                                <ValueCell
                                    value={row.ledgerRepayment}
                                    prev={prev.ledgerRepayment}
                                    unit={unit}
                                />
                                <ValueCell
                                    value={row.bank}
                                    prev={prev.bank}
                                    unit={unit}
                                />
                                <ValueCell
                                    value={row.bankDebit}
                                    prev={prev.bankDebit}
                                    unit={unit}
                                />
                                <td>
                                    <Pct value={matchPct(row.gst, row.ledger)} />
                                </td>
                                <td>
                                    <Pct
                                        value={matchPct(
                                            row.bank,
                                            row.ledgerRepayment
                                        )}
                                    />
                                </td>
                                <td>
                                    <FilingDelay value={row.filingDelay} />
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
                <tfoot>
                    <tr className={"cc-tri-table__total"}>
                        <td className={"cc-tri-table__month"}>Total</td>
                        <td>{formatAmount(totals.gst, unit) || DASH}</td>
                        <td>{formatAmount(totals.gstPurchase, unit) || DASH}</td>
                        <td>{formatAmount(totals.ledger, unit) || DASH}</td>
                        <td>
                            {formatAmount(totals.ledgerRepayment, unit) || DASH}
                        </td>
                        <td>{formatAmount(totals.bank, unit) || DASH}</td>
                        <td>{formatAmount(totals.bankDebit, unit) || DASH}</td>
                        <td>
                            <Pct value={matchPct(totals.gst, totals.ledger)} />
                        </td>
                        <td>
                            <Pct
                                value={matchPct(
                                    totals.bank,
                                    totals.ledgerRepayment
                                )}
                            />
                        </td>
                        {/* Filing delay has no meaningful total (it is a
                            per-month figure, not additive), so leave it blank. */}
                        <td>{DASH}</td>
                    </tr>
                </tfoot>
            </table>
        </div>
    );
};

TriangulationTable.propTypes = {
    data: PropTypes.array.isRequired,
    unit: PropTypes.string
};

export default TriangulationTable;
